import React, { useState } from 'react';
import { OnboardingStatusResponse } from '../types';

interface OnboardingProfileProps {
  token: string;
  onboardingData: OnboardingStatusResponse;
  onStepComplete: (step: string, data?: Record<string, unknown>) => Promise<void>;
  error: string | null;
}

/**
 * Profile completion step for Fair Manager onboarding
 * Issue #1455 - Step 3 of 7: Display name, bio, avatar (optional)
 */
const OnboardingProfile: React.FC<OnboardingProfileProps> = ({
  onboardingData,
  onStepComplete,
}) => {
  const savedProfile = onboardingData.onboarding.progress_data?.profile || {};

  const [displayName, setDisplayName] = useState(savedProfile.display_name || '');
  const [bio, setBio] = useState(savedProfile.bio || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!displayName.trim()) {
      setError('Display name is required');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      await onStepComplete('profile', {
        display_name: displayName.trim(),
        bio: bio.trim(),
      });

      // Move to next step
      await onStepComplete('contact');
    } catch (err) {
      setError('Failed to save profile. Please try again.');
      console.error('Failed to save profile:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = async () => {
    try {
      setLoading(true);
      await onStepComplete('contact');
    } catch (err) {
      console.error("Failed to skip profile:", err);
      setError('Failed to proceed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-8">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Complete Your Profile</h2>
        <p className="text-gray-600">
          Tell vendors a bit about yourself. This information will be visible on your fair listings.
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Display Name */}
        <div className="mb-6">
          <label htmlFor="displayName" className="block text-sm font-medium text-gray-700 mb-2">
            Display Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="displayName"
            value={displayName}
            onChange={(e) => {
              setDisplayName(e.target.value);
              setError(null);
            }}
            placeholder="e.g., Jane Smith or Smith Family Fairs"
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            aria-required="true"
            aria-describedby="displayName-description"
          />
          <p id="displayName-description" className="mt-1 text-sm text-gray-500">
            This is how vendors will see your name on fair listings
          </p>
        </div>

        {/* Bio */}
        <div className="mb-6">
          <label htmlFor="bio" className="block text-sm font-medium text-gray-700 mb-2">
            Bio (Optional)
          </label>
          <textarea
            id="bio"
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            rows={4}
            placeholder="Tell vendors about your experience managing fairs, your mission, or what makes your events special..."
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            aria-describedby="bio-description"
          />
          <p id="bio-description" className="mt-1 text-sm text-gray-500">
            Share your background and what vendors can expect from your fairs
          </p>
        </div>

        {/* Avatar placeholder */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Profile Photo (Optional - Coming Soon)
          </label>
          <div className="border-2 border-dashed border-gray-300 rounded-md p-6 text-center">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
              />
            </svg>
            <p className="mt-2 text-sm text-gray-500">Photo upload coming in a future update</p>
          </div>
        </div>

        {/* Error message */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4" role="alert">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-between items-center">
          <button
            type="button"
            onClick={handleSkip}
            disabled={loading}
            className="text-gray-600 hover:text-gray-800 font-medium disabled:opacity-50"
          >
            Skip for now
          </button>
          <button
            type="submit"
            disabled={loading}
            className={`
              px-6 py-2 rounded-md font-medium transition-colors
              ${
                loading
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-teal-600 text-white hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500'
              }
            `}
          >
            {loading ? 'Saving...' : 'Save and Continue'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default OnboardingProfile;
